import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true, // Crucial para enviar cookies httpOnly
});

// --- GESTOR DE TOKENS EN MEMORIA ---
let inMemoryAccessToken = null;

export const setAccessToken = (accessToken) => {
  inMemoryAccessToken = accessToken;
};

export const clearAccessToken = () => {
  inMemoryAccessToken = null;
};

// --- INTERCEPTOR DE PETICIONES ---
api.interceptors.request.use(
  (config) => {
    if (inMemoryAccessToken) {
      config.headers.Authorization = `Bearer ${inMemoryAccessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// --- INTERCEPTOR DE RESPUESTAS (LÓGICA DE REFRESH) ---
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // --- CORRECCIÓN: No intentar refresh si la ruta es /auth/login ---
    // Si el error es 401 y NO es la ruta de login, intentamos refresh
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/auth/login')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Intento de refrescar token usando la cookie de sesión
        const { data } = await api.post('/auth/refresh');
        const newAccessToken = data.accessToken;

        setAccessToken(newAccessToken);
        processQueue(null, newAccessToken);

        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAccessToken();
        window.dispatchEvent(new Event('auth-error'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Helper para subida directa a MinIO usando Presigned URLs
 */
export const uploadFileToPresignedUrl = async (
  presignedUrl,
  file,
  onUploadProgress
) => {
  try {
    const response = await axios.put(presignedUrl, file, {
      headers: { 'Content-Type': file.type },
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onUploadProgress(percentCompleted);
        }
      },
    });
    return { success: true, status: response.status };
  } catch (error) {
    console.error('Error al subir archivo:', error);
    return { success: false, error };
  }
};

export default api;
